import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../../modules/layout/components/ToastContext';
import { addTemplate, setTemplatePinned } from '../transactionExplorerService';
import { ITransactionTemplate, TemplateDirection } from '../types';
import AutoGrowTextarea from './AutoGrowTextarea';
import PayloadView from './PayloadView';
import { formatDateTime } from '../../../helpers/DateTimeHelper';

interface TemplateModalProps {
  show: boolean;
  className: string;
  templates: ITransactionTemplate[];
  loading: boolean;
  onClose: () => void;
  onTemplatesChanged?: () => void | Promise<void>;
}

const TemplateModal: React.FC<TemplateModalProps> = ({
  show,
  className,
  templates,
  loading,
  onClose,
  onTemplatesChanged,
}) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const modalRef = useRef<HTMLDivElement>(null);
  const bsModalRef = useRef<any>(null);
  const [direction, setDirection] = useState<TemplateDirection>(TemplateDirection.Request);
  const [rankIndex, setRankIndex] = useState(0);
  const [pinning, setPinning] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newPayload, setNewPayload] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (modalRef.current && !bsModalRef.current) {
      bsModalRef.current = new (window as any).bootstrap.Modal(modalRef.current, { backdrop: true });
      modalRef.current.addEventListener('hidden.bs.modal', onClose);
    }
    if (bsModalRef.current) {
      show ? bsModalRef.current.show() : bsModalRef.current.hide();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const current = useMemo(
    () =>
      templates
        .filter((tpl) => tpl.direction === direction)
        .sort((a, b) => a.rank - b.rank),
    [templates, direction]
  );

  useEffect(() => setRankIndex(0), [direction]);

  const selected = current[rankIndex];

  const handleTogglePin = async () => {
    if (!selected) return;
    const nextPinned = !selected.isPinned;
    setPinning(true);
    try {
      await setTemplatePinned(selected.id, nextPinned);
      showToast(nextPinned ? t('TemplatePinned') : t('TemplateUnpinned'), 'success');
      await onTemplatesChanged?.();
    } catch (e) {
      showToast(t('ConnectionError', { error: String(e) }), 'error');
    } finally {
      setPinning(false);
    }
  };

  const handleAddTemplate = async () => {
    const payload = newPayload.trim();
    const firstChar = payload.charAt(0);
    // Same detection the backend uses: only XML/JSON can be cleaned into a template.
    if (firstChar !== '<' && firstChar !== '{' && firstChar !== '[') {
      showToast(t('TemplatePayloadInvalid'), 'warning');
      return;
    }
    setSaving(true);
    try {
      await addTemplate(className, direction, payload);
      showToast(t('TemplateAdded'), 'success');
      setNewPayload('');
      setAdding(false);
      await onTemplatesChanged?.();
    } catch (e) {
      showToast(t('ConnectionError', { error: String(e) }), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal fade" tabIndex={-1} ref={modalRef} aria-hidden="true">
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {t('RequestResponseTemplate')} — <span className="text-muted">{className}</span>
            </h5>
            <button type="button" className="btn-close" onClick={() => bsModalRef.current?.hide()} aria-label="Close" />
          </div>
          <div className="modal-body">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <ul className="nav nav-tabs" style={{ borderBottom: 'none' }}>
                <li className="nav-item">
                  <button
                    className={`nav-link ${direction === TemplateDirection.Request ? 'active' : ''}`}
                    onClick={() => setDirection(TemplateDirection.Request)}
                  >
                    {t('Request')}
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${direction === TemplateDirection.Response ? 'active' : ''}`}
                    onClick={() => setDirection(TemplateDirection.Response)}
                  >
                    {t('Response')}
                  </button>
                </li>
              </ul>
              <button
                type="button"
                className={`btn btn-sm ${adding ? 'btn-outline-secondary' : 'btn-outline-dark'}`}
                onClick={() => setAdding((a) => !a)}
              >
                <i className={`bi ${adding ? 'bi-x-lg' : 'bi-plus-lg'} me-1`} />
                {adding ? t('Cancel') : t('AddTemplate')}
              </button>
            </div>

            {adding && (
              <div className="card mb-3">
                <div className="card-body">
                  <label className="form-label small text-muted">
                    {t('AddTemplateHint', {
                      direction: direction === TemplateDirection.Request ? t('Request') : t('Response'),
                    })}
                  </label>
                  <AutoGrowTextarea
                    className="form-control font-monospace mb-2"
                    minRows={8}
                    value={newPayload}
                    onChange={(e) => setNewPayload(e.target.value)}
                    placeholder={t('PastePayloadPlaceholder')}
                  />
                  <button
                    type="button"
                    className="btn btn-sm gg-btn-black"
                    disabled={saving || !newPayload.trim()}
                    onClick={handleAddTemplate}
                  >
                    <i className="bi bi-magic me-1" />
                    {t('CleanAndAdd')}
                  </button>
                </div>
              </div>
            )}

            {loading ? (
              <div className="text-muted">{t('Loading')}</div>
            ) : current.length === 0 ? (
              <div className="text-muted fst-italic">{t('NoTemplateAvailable')}</div>
            ) : (
              <>
                {current.length > 1 && (
                  <div className="btn-group btn-group-sm mb-2" role="group">
                    {current.map((tpl, i) => (
                      <button
                        key={tpl.rank}
                        type="button"
                        className={`btn ${i === rankIndex ? 'btn-secondary' : 'btn-outline-secondary'}`}
                        onClick={() => setRankIndex(i)}
                      >
                        {tpl.isPinned && <i className="bi bi-pin-angle-fill me-1" />}
                        {t('TemplateRank', { rank: tpl.rank })} ({tpl.occurrenceCount})
                      </button>
                    ))}
                  </div>
                )}
                {selected && (
                  <>
                    <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap mb-2">
                      <div className="small text-muted">
                        {selected.occurrenceCount > 0 ? (
                          t('SeenTimes', { count: selected.occurrenceCount })
                        ) : (
                          <span className="badge bg-secondary-subtle text-dark border">
                            <i className="bi bi-pencil-square me-1" />
                            {t('Manual')}
                          </span>
                        )}
                        {selected.generatedAt && (
                          <>
                            {' · '}
                            {t('GeneratedAt')}:{' '}
                            {formatDateTime(selected.generatedAt)}
                          </>
                        )}
                        {selected.isPinned && (
                          <span className="badge bg-dark ms-2">
                            <i className="bi bi-pin-angle-fill me-1" />
                            {t('Pinned')}
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        className={`btn btn-sm ${selected.isPinned ? 'btn-outline-secondary' : 'gg-btn-black'}`}
                        disabled={pinning}
                        onClick={handleTogglePin}
                        title={t('PinTemplateHint')}
                      >
                        <i className={`bi ${selected.isPinned ? 'bi-pin-angle' : 'bi-pin-angle-fill'} me-1`} />
                        {selected.isPinned ? t('Unpin') : t('Pin')}
                      </button>
                    </div>
                    <PayloadView body={selected.templateBody} />
                  </>
                )}
              </>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={() => bsModalRef.current?.hide()}>
              {t('Close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateModal;
